import { nanoid } from 'nanoid';

import { type SelectCellEvent } from '../events/types';
import { type SelectionRange, type SelectionState } from '../state/types';

import { type ReducerResponse } from './types';

/**
 * Handles a standard click on a cell.
 *
 * This is the "nuclear" option: wipe everything, start fresh with just this cell.
 * No matter how complex the current selection state is (multi-select, ranges, etc.),
 * clicking a cell resets us to a single cell selection with focus there.
 *
 * The delta tells effects "forget what you knew, here's the new truth".
 * We include focusChange so effects can animate the box moving, update ARIA, etc.
 */
export function selectCellReducer(
  state: SelectionState,
  event: SelectCellEvent
): ReducerResponse {
  const newSelection: SelectionRange = {
    id: nanoid(),
    anchor: event.cell,
    lead: event.cell
  };

  return {
    delta: {
      kind: 'selectionReplaced',
      source: event.source,
      selections: [newSelection],
      contextMenuTrigger: event.contextMenuTrigger,
      focusChange: {
        prev: state.focusedCell,
        next: event.cell
      }
    },
    next: {
      ...state,
      mode: 'cell',
      focusedCell: event.cell,
      selections: [newSelection],
      lastInteractionSource: event.source
    }
  };
}
